import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const hormonalTesting: ContentBlock<"procedure"> = {
  id: makeId("proc-hormonal-testing"),
  kind: "procedure",
  tags: ["testing", "her", "lab"],
  title: "Hormonal testing",
  subtitle:
    "Blood tests to evaluate reproductive hormones that influence fertility",
  body: {
    procedureName: "Hormonal testing",
    category: "diagnostic",
    description:
      "A panel of blood tests that measures key reproductive hormones including FSH, LH, estradiol, progesterone, and AMH to assess ovarian function and overall fertility potential.",
    howItWorks:
      "A small blood sample is drawn from a vein in your arm, usually in the morning. Depending on the hormones being tested, your doctor may schedule the draw on a specific cycle day. For example, FSH and estradiol are typically tested on cycle day 2 or 3, while progesterone is checked about a week after ovulation. AMH can be drawn on any cycle day. The samples are sent to a laboratory where each hormone level is measured and compared against reference ranges for your age and cycle phase.",
    duration: "5–10 minutes for the blood draw",
    anesthesia: "none",
    whatToExpect: [
      "A quick blood draw from your arm, similar to any routine lab work",
      "You may feel a brief pinch when the needle is inserted",
      "Some panels require fasting, so check with your clinic beforehand",
      "Results are typically available within 1–3 business days",
      "Your doctor will review the results and explain what each level means for your fertility plan",
    ],
    results: [
      {
        label: "When you hear",
        detail:
          "Most hormone panels return in 1-3 business days, though timing varies by lab and clinic portal.",
      },
      {
        label: "What you learn",
        detail:
          "Results may include AMH, FSH, LH, estradiol, progesterone, thyroid, or prolactin depending on what your doctor ordered.",
      },
      {
        label: "Context matters",
        detail:
          "Cycle day, medications, pregnancy status, and lab reference ranges can change how a hormone result is interpreted.",
      },
    ],
    prepInstructions: [
      "Confirm with your clinic which cycle day to schedule the blood draw",
      "Ask whether you need to fast before the test",
      "Stay well-hydrated the day before to make the blood draw easier",
      "Let your clinic know about any medications or supplements you are taking, as some can affect hormone levels",
    ],
    risks: [
      "Minor bruising or soreness at the needle site",
      "Very rare: lightheadedness during or after the blood draw",
    ],
    forWhom: "her",
    frBody: {
      procedureName: "Bilan hormonal",
      description:
        "Bilan sanguin évaluant les principales hormones de la reproduction (FSH, LH, estradiol, progestérone, AMH) afin d'apprécier la fonction ovarienne et le potentiel de fertilité global.",
      howItWorks:
        "Une petite prise de sang est effectuée au niveau du bras, généralement le matin. Selon les hormones dosées, votre médecin peut programmer le prélèvement un jour précis du cycle. Par exemple, la FSH et l'estradiol se dosent habituellement au 2e ou 3e jour du cycle, la progestérone environ une semaine après l'ovulation. L'AMH peut être prélevée à n'importe quel moment du cycle. Les échantillons sont analysés en laboratoire et chaque taux est comparé aux valeurs de référence pour votre âge et la phase du cycle.",
      duration: "5 à 10 minutes pour la prise de sang",
      whatToExpect: [
        "Prise de sang rapide au bras, comme un examen biologique de routine",
        "Vous pouvez ressentir une brève piqûre lors de l'insertion de l'aiguille",
        "Certains bilans nécessitent d'être à jeun, vérifiez auprès de votre centre",
        "Résultats généralement disponibles sous 1 à 3 jours ouvrés",
        "Votre médecin examinera les résultats et expliquera ce que signifie chaque taux pour votre projet",
      ],
      results: [
        {
          label: "Quand vous avez des nouvelles",
          detail:
            "La plupart des bilans hormonaux reviennent sous 1 à 3 jours ouvrés, selon le laboratoire et le portail du centre.",
        },
        {
          label: "Ce que vous apprenez",
          detail:
            "Les résultats peuvent inclure AMH, FSH, LH, estradiol, progestérone, thyroïde ou prolactine selon la prescription.",
        },
        {
          label: "Le contexte compte",
          detail:
            "Le jour du cycle, les traitements, une grossesse et les valeurs de référence du laboratoire peuvent modifier l'interprétation.",
        },
      ],
      prepInstructions: [
        "Confirmez avec votre centre le jour du cycle où réaliser le prélèvement",
        "Vérifiez si vous devez être à jeun",
        "Buvez bien la veille pour faciliter le prélèvement",
        "Signalez à votre centre les traitements ou compléments que vous prenez, certains pouvant influencer les taux hormonaux",
      ],
      risks: [
        "Léger hématome ou sensibilité au point de ponction",
        "Très rare : sensation de malaise pendant ou après la prise de sang",
      ],
    },
  },
  sources: [
    {
      title:
        "Testing and interpreting measures of ovarian reserve: a committee opinion",
      authors: "Practice Committee of the ASRM",
      journal: "Fertility and Sterility",
      year: 2020,
      url: "https://www.asrm.org/practice-guidance/practice-committee-documents/testing-and-interpreting-measures-of-ovarian-reserve-a-committee-opinion-2020/",
      evidenceLevel: "guideline",
    },
  ],
};

registerBlock(hormonalTesting);
